import { Badge, Button, Card, CardContent, Label, Spinner, Switch, toast } from "@veritrellis/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import type { ConnectorPlatform } from "@veritrellis/shared/connectors";
import { getConnectorCatalogEntry } from "@veritrellis/shared/connectors";
import { ConnectorCatalogModal } from "../components/connectors/ConnectorCatalogModal.js";
import {
  buildInitialSetupDraft,
  ConnectorSetupForm,
  parseDomainAllowlist,
  type ConnectorSetupDraft
} from "../components/connectors/ConnectorSetupForm.js";
import { apiFetch } from "../lib/api.js";
import { useAccessManifest } from "../hooks/useAccessManifest.js";
import { roleAtLeast, useCurrentRole } from "../hooks/useCurrentRole.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

type ConnectorRow = {
  id: string;
  environment: string;
  platform: string;
  label: string;
  enabled: boolean;
  inbound_auth_type: string;
  outbound_base_url: string;
  last_tested_at: string | null;
  last_success_at: string | null;
};

type ModalStep = "closed" | "catalog" | "setup";

export const ConnectorsPage = () => {
  const { workspaceId, isLoading: widLoading } = useWorkspaceId();
  const { role } = useCurrentRole();
  const manifestQ = useAccessManifest(workspaceId);
  const queryClient = useQueryClient();
  const canConfigure = roleAtLeast(role, "developer");

  const canMutateSandbox = manifestQ.data?.manifest.can.mutate_connectors_sandbox === true;
  const canMutateProduction = manifestQ.data?.manifest.can.mutate_connectors_production === true;
  const allowProduction = canMutateProduction;
  const canMutateAnyConnector = canMutateSandbox || canMutateProduction;

  const [modalStep, setModalStep] = useState<ModalStep>("closed");
  const [selectedPlatform, setSelectedPlatform] = useState<ConnectorPlatform | null>(null);
  const [setupDraft, setSetupDraft] = useState<ConnectorSetupDraft | null>(null);

  const selectedEntry = useMemo(() => {
    if (selectedPlatform === null) {
      return null;
    }
    return getConnectorCatalogEntry(selectedPlatform) ?? null;
  }, [selectedPlatform]);

  const listQ = useQuery({
    queryKey: ["connectors", workspaceId],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/connectors`);
      if (!res.ok) {
        throw new Error(`connectors_${String(res.status)}`);
      }
      return (await res.json()) as { connectors: ConnectorRow[] };
    }
  });

  const [logConnectorId, setLogConnectorId] = useState<string | null>(null);

  const logsQ = useQuery({
    queryKey: ["connector-logs", workspaceId, logConnectorId],
    enabled: workspaceId !== null && logConnectorId !== null,
    queryFn: async () => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/connectors/${logConnectorId}/request-logs?limit=50`
      );
      if (!res.ok) {
        throw new Error(`logs_${String(res.status)}`);
      }
      return (await res.json()) as {
        logs: Array<{
          id: string;
          correlation_id: string;
          action_template: string;
          authorization_request_id: string | null;
          decision: string;
          created_at: string;
        }>;
      };
    }
  });

  const openAddModal = () => {
    setModalStep("catalog");
    setSelectedPlatform(null);
    setSetupDraft(null);
  };

  const closeModal = () => {
    setModalStep("closed");
    setSelectedPlatform(null);
    setSetupDraft(null);
  };

  const pickPlatform = (platform: ConnectorPlatform) => {
    const entry = getConnectorCatalogEntry(platform);
    if (entry === undefined) {
      return;
    }
    const initial = buildInitialSetupDraft({ platform, entry, defaultEnvironment: "sandbox" });
    const clamped =
      !allowProduction && initial.environment === "production"
        ? { ...initial, environment: "sandbox" as const }
        : initial;
    setSelectedPlatform(platform);
    setSetupDraft(clamped);
    setModalStep("setup");
  };

  const backToCatalog = () => {
    setSelectedPlatform(null);
    setSetupDraft(null);
    setModalStep("catalog");
  };

  const createConnector = useMutation({
    mutationFn: async (draft: ConnectorSetupDraft) => {
      if (workspaceId === null || selectedPlatform === null) {
        throw new Error("missing_context");
      }
      if (draft.environment === "production" && !allowProduction) {
        throw new Error("forbidden_env");
      }
      if (draft.environment === "sandbox" && !canMutateSandbox) {
        throw new Error("forbidden_env");
      }
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/connectors`, {
        method: "POST",
        body: JSON.stringify({
          environment: draft.environment,
          platform: selectedPlatform,
          label: draft.label.trim(),
          inbound_auth_type: draft.inbound_auth_type,
          outbound_base_url: draft.outbound_base_url.trim(),
          outbound_auth_type: draft.outbound_auth_type,
          domain_allowlist: parseDomainAllowlist(draft.domainAllowlistInput)
        })
      });
      if (!res.ok) {
        throw new Error(`create_${String(res.status)}`);
      }
      return (await res.json()) as { connector: { invoke_path: string } };
    },
    onSuccess: (data) => {
      closeModal();
      void queryClient.invalidateQueries({ queryKey: ["connectors", workspaceId] });
      toast.show({
        title: "Connector saved",
        message: `Invoke path: ${data.connector.invoke_path}`,
        color: "green"
      });
    },
    onError: (err: Error) => {
      const msg =
        err.message === "forbidden_env"
          ? "You cannot create connectors in that environment for this workspace."
          : "Could not save connector.";
      toast.show({ title: "Error", message: msg, color: "red" });
    }
  });

  const setEnabled = useMutation({
    mutationFn: async (input: { connectorId: string; enabled: boolean }) => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/connectors/${input.connectorId}/enabled`,
        {
          method: "PATCH",
          body: JSON.stringify({ enabled: input.enabled })
        }
      );
      if (!res.ok) {
        throw new Error(`enabled_${String(res.status)}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["connectors", workspaceId] });
      toast.show({
        title: "Updated",
        message: "Connector enabled flag saved.",
        color: "green"
      });
    },
    onError: () => {
      toast.show({
        title: "Error",
        message: "Could not update enabled state.",
        color: "red"
      });
    }
  });

  const testRelay = useMutation({
    mutationFn: async (connectorId: string) => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/connectors/${connectorId}/test-relay`,
        { method: "POST", body: JSON.stringify({ dry_run: true }) }
      );
      if (!res.ok) {
        throw new Error(`test_${String(res.status)}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["connectors", workspaceId] });
      toast.show({
        title: "Probe recorded",
        message: "Last tested timestamp updated.",
        color: "green"
      });
    },
    onError: () => {
      toast.show({
        title: "Error",
        message: "Relay probe failed (check allowlist and URL).",
        color: "red"
      });
    }
  });

  if (widLoading || manifestQ.isLoading) {
    return <p className="text-sm">Loading…</p>;
  }
  if (manifestQ.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load access manifest. Refresh and try again.
      </p>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (listQ.isLoading) {
    return (
      <div className="flex min-h-[160px] items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (listQ.isError) {
    return <p className="text-sm text-destructive">Failed to load connectors.</p>;
  }

  const connectors = listQ.data?.connectors ?? [];
  const modalOpen = modalStep !== "closed";
  const innerStep: "catalog" | "setup" = modalStep === "setup" ? "setup" : "catalog";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">Connectors</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Hosted inbound URL per installation; outbound relay runs after allow or approval per
            policy.
          </p>
        </div>
        {canConfigure && canMutateAnyConnector ? (
          <Button onClick={() => openAddModal()}>Add connector</Button>
        ) : null}
      </div>

      {connectors.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-start gap-4 p-8">
            <h3 className="text-lg font-semibold">Set up connectors</h3>
            <p className="max-w-lg text-sm text-muted-foreground">
              Connect HubSpot, Intercom, or Zendesk to Veritrellis. Each installation gets its own
              invoke URL and outbound relay settings.
            </p>
            {canConfigure && canMutateAnyConnector ? (
              <Button onClick={() => openAddModal()}>Add connector</Button>
            ) : canConfigure && !canMutateAnyConnector ? (
              <p className="text-sm text-muted-foreground">
                You do not have permission to create connectors in any environment for this
                workspace.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Your role cannot configure connectors. Ask a developer, admin, or owner.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {connectors.map((c) => (
            <Card key={c.id}>
              <CardContent className="flex flex-col gap-3 pt-6">
                <div className="flex flex-nowrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{c.label}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      <Badge variant="secondary">{c.platform}</Badge>
                      <Badge variant={c.environment === "production" ? "warning" : "outline"}>
                        {c.environment}
                      </Badge>
                    </div>
                  </div>
                  {canConfigure ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <Label className="text-xs">Enabled</Label>
                      <Switch
                        checked={c.enabled}
                        disabled={setEnabled.isPending}
                        onCheckedChange={(checked) => {
                          setEnabled.mutate({ connectorId: c.id, enabled: checked === true });
                        }}
                      />
                    </div>
                  ) : (
                    <Badge variant={c.enabled ? "success" : "secondary"}>
                      {c.enabled ? "enabled" : "disabled"}
                    </Badge>
                  )}
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  Outbound: {c.outbound_base_url}
                </p>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span>
                    Last tested:{" "}
                    {c.last_tested_at !== null ? new Date(c.last_tested_at).toLocaleString() : "—"}
                  </span>
                  <span>
                    Last success:{" "}
                    {c.last_success_at !== null
                      ? new Date(c.last_success_at).toLocaleString()
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setLogConnectorId(c.id)}>
                    Logs
                  </Button>
                  {canConfigure ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={testRelay.isPending}
                      onClick={() => testRelay.mutate(c.id)}
                    >
                      Test relay
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConnectorCatalogModal
        opened={modalOpen}
        step={innerStep}
        catalogEntry={selectedEntry}
        enabledPlatforms={manifestQ.data?.enabled_connector_platforms}
        onClose={() => closeModal()}
        onPickPlatform={(p) => pickPlatform(p)}
        onBackToCatalog={() => backToCatalog()}
        setupBody={
          selectedEntry !== null && setupDraft !== null ? (
            <ConnectorSetupForm
              entry={selectedEntry}
              draft={setupDraft}
              allowProduction={allowProduction}
              onDraftChange={(next) => {
                setSetupDraft(next);
              }}
            />
          ) : null
        }
        setupActions={
          <Button
            disabled={setupDraft === null || createConnector.isPending}
            onClick={() => {
              if (setupDraft !== null) {
                createConnector.mutate(setupDraft);
              }
            }}
          >
            {createConnector.isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
            Save connector
          </Button>
        }
      />

      {logConnectorId !== null ? (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-semibold">Request logs</p>
              <Button size="sm" variant="outline" onClick={() => setLogConnectorId(null)}>
                Close
              </Button>
            </div>
            {logsQ.isLoading ? <Spinner className="h-5 w-5" /> : null}
            {logsQ.isError ? (
              <p className="text-sm text-destructive">Failed to load logs.</p>
            ) : null}
            {logsQ.data !== undefined ? (
              <div className="flex flex-col gap-2">
                {logsQ.data.logs.map((l) => (
                  <div key={l.id} className="rounded-md border p-2">
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="text-xs">{new Date(l.created_at).toLocaleString()}</span>
                      <Badge variant="outline" className="text-xs">
                        {l.decision}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm">{l.action_template}</p>
                    <p className="truncate font-mono text-xs text-muted-foreground">
                      {l.authorization_request_id ?? "—"}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
